import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { authFetch } from "./AuthContext";

// Step 7.6 (2026-02-15) — Platform Settings context.
//
// Fetches the client-safe subset of platform_settings (idle timeout,
// per-member reveal duration, hover delay) as soon as a user is
// authenticated. All child components that need a timing knob read it
// from this context rather than hardcoding milliseconds.
//
// The Platform Admin "Settings" modal updates the DB via PATCH and calls
// `refresh()` to re-pull the values so every consumer picks up the new
// number on the next tick — no page reload required.

const API = process.env.REACT_APP_BACKEND_URL;

// Sensible fallbacks — used ONLY on the very first render before the fetch
// completes, or if the endpoint fails. Match the DB defaults so the app
// feels consistent even in the failure case.
const DEFAULTS = {
  session_idle_minutes: 30,
  session_expiring_warning_seconds: 60,
  per_member_reveal_seconds: 5,
  hover_reveal_seconds: 5,
  hover_visible_seconds: 5,
  // Idle-back-to-Dashboard (2026-02-19) — snap non-Dashboard tabs back
  // to Dashboard after this many seconds of no interaction.
  idle_dashboard_snapback_seconds: 30,
  // Password policy (Step 7.7 · 2026-02-15) — mirrored to the client so the
  // ChangePasswordModal can give instant feedback matching what the server
  // will enforce. Booleans encoded as 0/1.
  password_min_length: 12,
  password_require_uppercase: 0,
  password_require_number: 0,
  password_require_special: 0,
  // Version Content (2026-03-03 · Dave) — per-tier switches that decide
  // which tabs render at startup. All default to 1 (visible) so no tab
  // silently disappears before the fetch completes.
  contributions_tab_basic: 1,
  contributions_tab_pro: 1,
  elements_tab_basic: 1,
  elements_tab_pro: 1,
  projections_tab_basic: 1,
  projections_tab_pro: 1,
  illustrations_tab_basic: 1,
  illustrations_tab_pro: 1,
  dash_elements_cost_basic: 1,
  dash_elements_cost_pro: 1,
  dash_waterfall_basic: 1,
  dash_waterfall_pro: 1,
  dash_funding_mix_basic: 1,
  dash_funding_mix_pro: 1,
  dash_funding_gauge_basic: 1,
  dash_funding_gauge_pro: 1,
  dash_activity_basic: 1,
  dash_activity_pro: 1,
  // Dashboard Activity chart column break (Platform Settings).
  chart_break_first_enabled: 1,
  chart_break_trigger_pct: 115,
  chart_break_second_enabled: 1,
  chart_break_second_trigger_pct: 130,
  chart_break_headroom_pct: 105,
  chart_break_position_pct: 85,
  dashboard_chart_height_pct: 80,
  dashboard_panel_gap_px: 27,
  dashboard_chart_gap_px: 24,
  dashboard_edge_side_px: 28,
  dashboard_edge_tb_px: 6,
};

const SettingsCtx = createContext({
  settings: DEFAULTS,
  refresh: async () => {},
  loaded: false,
});

export function SettingsProvider({ children, isAuthenticated }) {
  const [settings, setSettings] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/api/platform-settings/public`);
      if (!res.ok) throw new Error(`settings fetch ${res.status}`);
      const data = await res.json();
      setSettings({
        session_idle_minutes: Number(data.session_idle_minutes ?? DEFAULTS.session_idle_minutes),
        session_expiring_warning_seconds: Number(data.session_expiring_warning_seconds ?? DEFAULTS.session_expiring_warning_seconds),
        per_member_reveal_seconds: Number(data.per_member_reveal_seconds ?? DEFAULTS.per_member_reveal_seconds),
        hover_reveal_seconds: Number(data.hover_reveal_seconds ?? DEFAULTS.hover_reveal_seconds),
        hover_visible_seconds: Number(data.hover_visible_seconds ?? DEFAULTS.hover_visible_seconds),
        idle_dashboard_snapback_seconds: Number(data.idle_dashboard_snapback_seconds ?? DEFAULTS.idle_dashboard_snapback_seconds),
        password_min_length: Number(data.password_min_length ?? DEFAULTS.password_min_length),
        password_require_uppercase: Number(data.password_require_uppercase ?? DEFAULTS.password_require_uppercase),
        password_require_number: Number(data.password_require_number ?? DEFAULTS.password_require_number),
        password_require_special: Number(data.password_require_special ?? DEFAULTS.password_require_special),
        contributions_tab_basic: Number(data.contributions_tab_basic ?? DEFAULTS.contributions_tab_basic),
        contributions_tab_pro: Number(data.contributions_tab_pro ?? DEFAULTS.contributions_tab_pro),
        elements_tab_basic: Number(data.elements_tab_basic ?? DEFAULTS.elements_tab_basic),
        elements_tab_pro: Number(data.elements_tab_pro ?? DEFAULTS.elements_tab_pro),
        projections_tab_basic: Number(data.projections_tab_basic ?? DEFAULTS.projections_tab_basic),
        projections_tab_pro: Number(data.projections_tab_pro ?? DEFAULTS.projections_tab_pro),
        illustrations_tab_basic: Number(data.illustrations_tab_basic ?? DEFAULTS.illustrations_tab_basic),
        illustrations_tab_pro: Number(data.illustrations_tab_pro ?? DEFAULTS.illustrations_tab_pro),
        dash_elements_cost_basic: Number(data.dash_elements_cost_basic ?? DEFAULTS.dash_elements_cost_basic),
        dash_elements_cost_pro: Number(data.dash_elements_cost_pro ?? DEFAULTS.dash_elements_cost_pro),
        dash_waterfall_basic: Number(data.dash_waterfall_basic ?? DEFAULTS.dash_waterfall_basic),
        dash_waterfall_pro: Number(data.dash_waterfall_pro ?? DEFAULTS.dash_waterfall_pro),
        dash_funding_mix_basic: Number(data.dash_funding_mix_basic ?? DEFAULTS.dash_funding_mix_basic),
        dash_funding_mix_pro: Number(data.dash_funding_mix_pro ?? DEFAULTS.dash_funding_mix_pro),
        dash_funding_gauge_basic: Number(data.dash_funding_gauge_basic ?? DEFAULTS.dash_funding_gauge_basic),
        dash_funding_gauge_pro: Number(data.dash_funding_gauge_pro ?? DEFAULTS.dash_funding_gauge_pro),
        dash_activity_basic: Number(data.dash_activity_basic ?? DEFAULTS.dash_activity_basic),
        dash_activity_pro: Number(data.dash_activity_pro ?? DEFAULTS.dash_activity_pro),
        chart_break_first_enabled: Number(data.chart_break_first_enabled ?? DEFAULTS.chart_break_first_enabled),
        chart_break_trigger_pct: Number(data.chart_break_trigger_pct ?? DEFAULTS.chart_break_trigger_pct),
        chart_break_second_enabled: Number(data.chart_break_second_enabled ?? DEFAULTS.chart_break_second_enabled),
        chart_break_second_trigger_pct: Number(data.chart_break_second_trigger_pct ?? DEFAULTS.chart_break_second_trigger_pct),
        chart_break_headroom_pct: Number(data.chart_break_headroom_pct ?? DEFAULTS.chart_break_headroom_pct),
        chart_break_position_pct: Number(data.chart_break_position_pct ?? DEFAULTS.chart_break_position_pct),
        dashboard_chart_height_pct: Number(data.dashboard_chart_height_pct ?? DEFAULTS.dashboard_chart_height_pct),
        dashboard_panel_gap_px: Number(data.dashboard_panel_gap_px ?? DEFAULTS.dashboard_panel_gap_px),
        dashboard_chart_gap_px: Number(data.dashboard_chart_gap_px ?? DEFAULTS.dashboard_chart_gap_px),
        dashboard_edge_side_px: Number(data.dashboard_edge_side_px ?? DEFAULTS.dashboard_edge_side_px),
        dashboard_edge_tb_px: Number(data.dashboard_edge_tb_px ?? DEFAULTS.dashboard_edge_tb_px),
      });
      setLoaded(true);
    } catch (e) {
      // Silent — keep the defaults; the DB is the source of truth so a
      // transient network hiccup shouldn't blow up the UI.
      console.warn("Platform settings fetch failed:", e?.message);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) refresh();
    else {
      setSettings(DEFAULTS);
      setLoaded(false);
    }
  }, [isAuthenticated, refresh]);

  return (
    <SettingsCtx.Provider value={{ settings, refresh, loaded }}>
      {children}
    </SettingsCtx.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsCtx);
}
